import React, { useEffect } from "react";
import "./App.css";
import { Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { appTheme } from "./theme";
import Layout from "./components/common/Layout";
import HomePage from "./components/Home/HomePage";
import Dashboard from "./components/Dashboard/Dashboard";
import { UserContext } from "./context/UserContext";
import Engagements from "./components/Dashboard/Engagements";
import Firms from "./components/Dashboard/Firms";
import FirmReport from "./components/FirmReport/FirmReport";
import ExploreFirm from "./components/Dashboard/ExploreFirm/ExploreFirm";
import MyReferrals from "./components/Dashboard/MyReferrals";
import Glossary from "./components/Dashboard/Glossary/Glossary";
import Providers from "./components/Dashboard/ExploreProviders/Providers";
import Case from "./components/Case/Case";
import { validate } from "./utils/auth";
import { useNavigate, useLocation } from "react-router-dom";
import ResourceList from "./components/Dashboard/Resources/Resources";
import Events from "./components/Dashboard/Events/Events";
import { UserType } from "./interfaces";
import PublicClientReferralSurvey from "./components/Survey/PublicClientReferralSurvey";
import { NavbarProvider } from "./context/NavbarContext";
import { Outlet } from "react-router-dom";
import PublicEventSurvey from "./components/Survey/PublicEventSurvey";
import PastEvents from "./components/Dashboard/Events/PastEvents";
import PublicConferenceSurvey from "./components/Survey/PublicConferenceSurvey";
import PublicB2BClientSurvey from "./components/Survey/PublicB2BClientSurvey";
import PublicB2BProviderSurvey from "./components/Survey/PublicB2BProviderSurvey";
import UserManagement from "./components/User/UserManagement";
import TagManagement from "./components/User/TagManagement";
import ReportPrint from "./components/Dashboard/Reports/ReportPrint";
import ExploreArchiveFirm from "./components/Dashboard/ExploreArchiveFirm/ExploreArchiveFirm";
import ArchiveCase from "./components/Dashboard/ExploreArchiveFirm/ArchiveCase";
import FirmReportPdf from "./components/FirmReport/FirmReportPdf";
import RGA_report from "./components/Dashboard/Reports/ReportItems/RGA_report";
import Report from "./components/Dashboard/Reports/Report";
import Unsubscribe from "./components/User/Unsubscribe";
import UnsubscribeContact from "./components/User/UnsubscribeContact";
import SetPassword from "./components/User/SetPassword";
import WebIntakeForms from "./components/Dashboard/WebIntakeForms/WebIntakeForms";
import DataValidations from "./components/Dashboard/DataValidations/DataValidations";
import BroadcastPage from "./components/Dashboard/Broadcast/BroadcastPage";
// Program Statistics, the legacy Reports index and the Executive dashboard
// are unmounted — their files stay for reference; the Reporting hub replaces
// them at /dashboard/reports.
import ReportingLayout from "./components/Dashboard/Reporting/ReportingLayout";
import InactiveClients from "./components/Dashboard/Inactive/InactiveClients";
import ConsultantPrintReport from "./components/Dashboard/Reporting/print/ConsultantPrintReport";
import SupportTickets from "./components/Dashboard/Support/SupportTickets";
import TicketDetail from "./components/Dashboard/Support/TicketDetail";
// Project governance (admins only; IC_Ticket project via server /ic-ticket/governance).
import GovernanceLayout from "./components/Dashboard/Governance/GovernanceLayout";
import TrackerRegister from "./components/Dashboard/Governance/registers/TrackerRegister";
import ChangeRequestRegister from "./components/Dashboard/Governance/registers/ChangeRequestRegister";
import IssueRegister from "./components/Dashboard/Governance/registers/IssueRegister";
import ReleaseRegister from "./components/Dashboard/Governance/registers/ReleaseRegister";
import UatRegister from "./components/Dashboard/Governance/registers/UatRegister";
import WeeklyStatusList from "./components/Dashboard/Governance/WeeklyStatusList";
import GovernanceItemDetail from "./components/Dashboard/Governance/ItemDetail";
import GovernanceWeeklyReport from "./components/Dashboard/Governance/WeeklyReport";
// import Survey_May_2023 from "./components/Survey/Survey_May_2023";
function App() {
  const [user, setUser] = React.useState<UserType>(() => {
    return JSON.parse(localStorage.getItem("user") || "{}");
  });

  useEffect(() => {
    localStorage.setItem("user", JSON.stringify(user));
  }, [user]);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    validate(location.pathname).then((res) => {
      if (!res) {
        navigate("/");
      }
    });
  }, [navigate]);

  return (
    <ThemeProvider theme={appTheme}>
      <UserContext.Provider value={{ user, setUser }}>
        <NavbarProvider>
          <div className="App">
            <Routes>
              <Route path="/" element={<Layout />}>
                <Route index element={<HomePage />}></Route>
                <Route path="/case" element={<Case />}></Route>
                <Route path="/firmReport" element={<FirmReport />}></Route>
                <Route path="/firmReportPdf" element={<FirmReportPdf />}></Route>
                {/* <Route path="/survey" element={<Survey_May_2023 />}></Route> */}
                <Route path="/dashboard" element={<Dashboard />}>
                  <Route index element={<Engagements />}></Route>
                  <Route path="validations" element={<DataValidations />}></Route>
                  <Route path="firms" element={<Firms />}></Route>
                  <Route
                    path="resources"
                    element={<ResourceList user={user} />}
                  ></Route>
                  <Route path="events" element={<Events user={user} />}></Route>
                  <Route
                    path="pastevents"
                    element={<PastEvents user={user} />}
                  ></Route>
                  <Route path="exploreFirm" element={<ExploreFirm />}></Route>
                  <Route path="myreferrals" element={<MyReferrals />}></Route>
                  <Route
                    path="inactive"
                    element={<InactiveClients />}
                  ></Route>
                  <Route path="glossary" element={<Glossary />}></Route>
                  <Route
                    path="providers"
                    element={<Providers user={user} />}
                  ></Route>
                  {/* Program Statistics + the Executive dashboard are folded
                      into the Reporting hub; the old paths redirect to their
                      tab so bookmarks keep working. */}
                  <Route
                    path="statistics"
                    element={
                      <Navigate to="/dashboard/reports?tab=consultants" replace />
                    }
                  ></Route>
                  <Route path="reports" element={<ReportingLayout />}></Route>
                  <Route path="reports/:id" element={<Report></Report>}></Route>
                  <Route
                    path="executive"
                    element={
                      <Navigate to="/dashboard/reports?tab=overview" replace />
                    }
                  ></Route>
                  <Route
                    path="archivedcases"
                    element={<ExploreArchiveFirm />}
                  ></Route>
                  <Route
                    path="archivedcases/:id"
                    element={<ArchiveCase />}
                  ></Route>
                  <Route
                    path="webIntakeForms"
                    element={<WebIntakeForms></WebIntakeForms>}
                  ></Route>
                  <Route
                    path="broadcast"
                    element={<BroadcastPage />}
                  ></Route>
                  <Route path="support" element={<SupportTickets />}></Route>
                  <Route path="support/:id" element={<TicketDetail />}></Route>
                  {/* Project governance: the workbook tabs share one layout;
                      item detail and the weekly report page sit outside it
                      (more specific static segments, so they rank first). */}
                  <Route path="governance" element={<GovernanceLayout />}>
                    <Route index element={<Navigate to="tracker" replace />}></Route>
                    <Route path="tracker" element={<TrackerRegister />}></Route>
                    <Route
                      path="change-requests"
                      element={<ChangeRequestRegister />}
                    ></Route>
                    <Route path="issues" element={<IssueRegister />}></Route>
                    <Route path="releases" element={<ReleaseRegister />}></Route>
                    <Route path="uat" element={<UatRegister />}></Route>
                    <Route
                      path="weekly-status"
                      element={<WeeklyStatusList />}
                    ></Route>
                  </Route>
                  <Route
                    path="governance/items/:id"
                    element={<GovernanceItemDetail />}
                  ></Route>
                  <Route
                    path="governance/weekly-status/:reportId"
                    element={<GovernanceWeeklyReport />}
                  ></Route>
                </Route>
                <Route path="/survey" element={<Outlet></Outlet>}>
                  <Route
                    path="clientReferralSurvey"
                    element={<PublicClientReferralSurvey />}
                  ></Route>
                  <Route
                    path="eventSurvey"
                    element={<PublicEventSurvey />}
                  ></Route>
                  <Route
                    path="conference"
                    element={<PublicConferenceSurvey></PublicConferenceSurvey>}
                  ></Route>
                  <Route
                    path="b2bclient"
                    element={<PublicB2BClientSurvey></PublicB2BClientSurvey>}
                  ></Route>
                  <Route
                    path="b2bprovider"
                    element={<PublicB2BProviderSurvey></PublicB2BProviderSurvey>}
                  ></Route>
                </Route>
                <Route
                  path="/users"
                  element={<UserManagement></UserManagement>}
                ></Route>
                <Route
                  path="/tags"
                  element={<TagManagement></TagManagement>}
                ></Route>
                <Route
                  path="/reportprint"
                  element={<ReportPrint></ReportPrint>}
                ></Route>
                {/* External (custom-list) contacts — declared before the firm
                    route; the extra static "contact" segment also ranks it
                    higher than /unsubscribe/:firmId in React Router 6. */}
                <Route
                  path="/unsubscribe/contact/:contactId"
                  element={<UnsubscribeContact></UnsubscribeContact>}
                ></Route>
                <Route
                  path="/unsubscribe/:firmId"
                  element={<Unsubscribe></Unsubscribe>}
                ></Route>
                <Route
                  path="/set-password"
                  element={<SetPassword></SetPassword>}
                ></Route>
              </Route>
              {/* Print-styled consultant report — mounted outside <Layout> so the
                  page carries no app chrome into the printout. */}
              <Route
                path="/reportprint/consultant"
                element={<ConsultantPrintReport></ConsultantPrintReport>}
              ></Route>
            </Routes>
          </div>
        </NavbarProvider>
      </UserContext.Provider>
    </ThemeProvider>
  );
}

export default App;
